import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardMedia, 
  IconButton, 
  CircularProgress,
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Fab,
  LinearProgress,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import { api } from '../context/AuthContext';
import { format } from 'date-fns';

const EditMemorial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [memorial, setMemorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  
  // Get API base URL from environment variable
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  
  console.log('EditMemorial using API base URL:', apiBaseUrl);
  
  // Memorial details edit state
  const [editForm, setEditForm] = useState({
    name: '',
    dateOfBirth: '',
    dateOfDeath: '',
    biography: ''
  });
  
  // Photo management states
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [photoDescription, setPhotoDescription] = useState('');
  const [savingChanges, setSavingChanges] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'url'
  
  // File upload ref
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // Helper function to ensure absolute URLs - with useCallback
  const ensureAbsoluteUrl = useCallback((url) => {
    if (!url) return '';
    
    // If it's already an absolute URL
    if (url.startsWith('http')) {
      return url;
    }
    
    // If it already includes the API base URL
    if (url.includes(apiBaseUrl)) {
      return url;
    }
    
    // If it's a relative URL, add the API base URL
    return `${apiBaseUrl}${url}`;
  }, [apiBaseUrl]);

  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        setLoading(true);
        console.log(`Attempting to fetch memorial with ID: ${id} from ${apiBaseUrl}/api/memorials/${id}`);
        
        const response = await api.get(`/api/memorials/${id}`);
        console.log('API response:', response);
        
        let memorialData;
        if (response.data && response.data.memorial) {
          memorialData = response.data.memorial;
        } else if (response.data) {
          memorialData = response.data;
        } else {
          throw new Error('Invalid response format from server');
        }
        
        // Process photo URLs to include the API base URL if they're relative paths
        if (memorialData.photos && memorialData.photos.length > 0) {
          console.log('Original photo URLs:', memorialData.photos.map(p => p.url));
          
          memorialData.photos = memorialData.photos.map(photo => ({
            ...photo,
            url: ensureAbsoluteUrl(photo.url)
          }));
          
          console.log('Processed photo URLs:', memorialData.photos.map(p => p.url));
        }
        
        setMemorial(memorialData);
        setPhotos(memorialData.photos || []);
        
        // Initialize edit form with current values
        setEditForm({
          name: memorialData.name || '',
          dateOfBirth: memorialData.dateOfBirth ? memorialData.dateOfBirth.split('T')[0] : '',
          dateOfDeath: memorialData.dateOfDeath ? memorialData.dateOfDeath.split('T')[0] : '',
          biography: memorialData.biography || ''
        });
      } catch (err) {
        console.error('Error fetching memorial:', err);
        setError(`Failed to load memorial: ${err.response?.data?.message || err.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    if (id) {
      fetchMemorial();
    } else {
      setError('No memorial ID provided');
      setLoading(false);
    }
  }, [id, apiBaseUrl, ensureAbsoluteUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any preview URLs when component unmounts
      previewUrls.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown';
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return dateString || 'Unknown';
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };
  
  // Toggle details editing mode
  const toggleDetailsEditing = () => {
    if (editingDetails) {
      // If we're currently editing and want to save
      handleSaveDetails();
    } else {
      // Start editing
      setEditingDetails(true);
    }
  };
  
  // Handle form changes for memorial details
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Save memorial details
  const handleSaveDetails = async () => {
    try {
      setSavingChanges(true);
      setSaveError('');
      
      const response = await api.put(`/api/memorials/${id}`, editForm);
      
      if (response.data) {
        // Update the memorial state with new data
        setMemorial(prev => ({
          ...prev,
          name: editForm.name,
          dateOfBirth: editForm.dateOfBirth,
          dateOfDeath: editForm.dateOfDeath,
          biography: editForm.biography
        }));
        
        setEditingDetails(false);
        alert('Memorial details updated successfully');
      }
    } catch (err) {
      console.error('Error updating memorial details:', err);
      setSaveError(`Failed to update details: ${err.response?.data?.message || err.message}`);
      alert('Failed to update memorial details. Please try again.');
    } finally {
      setSavingChanges(false);
    }
  };

  // Photo management functions
  const handleOpenPhotoDialog = () => {
    setNewPhotoUrl('');
    setPhotoDescription('');
    setSelectedFiles([]);
    setPreviewUrls([]);
    setUploadProgress(0);
    setSaveError('');
    setOpenPhotoDialog(true);
  };

  const handleClosePhotoDialog = () => {
    // Clean up preview URLs
    previewUrls.forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    setPreviewUrls([]);
    setOpenPhotoDialog(false);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Clean up any existing preview URLs first
    previewUrls.forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });

    setSelectedFiles(files);
    
    // Create preview URLs for all selected files
    const newPreviewUrls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(newPreviewUrls);
  };

  const handleUploadMethodChange = (method) => {
    setUploadMethod(method);
    // Reset fields
    setNewPhotoUrl('');
    setSelectedFiles([]);
    
    // Clean up any existing preview URLs
    previewUrls.forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    setPreviewUrls([]);
  };

  const uploadFilesToServer = async (files) => {
    // Create a FormData object to send multiple files
    const formData = new FormData();
    formData.append('memorialId', id);
    
    // Append all files
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add description if available (will apply to all photos)
    if (photoDescription) {
      formData.append('description', photoDescription);
    }
    
    try {
      // Make the upload request with progress tracking
      const response = await api.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });
      
      // Return array of uploaded photo URLs or objects
      if (response.data && response.data.photos && response.data.photos.length > 0) {
        return response.data.photos;
      } else if (response.data && response.data.photo) {
        return [response.data.photo];
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  const handleAddPhoto = async () => {
    try {
      setSavingChanges(true);
      setSaveError('');
      
      let newPhotos = [];
      
      if (uploadMethod === 'file') {
        if (!selectedFiles.length) {
          setSaveError('Please select files to upload');
          setSavingChanges(false);
          return;
        }
        
        // Upload the files and get the photo objects or URLs
        const uploadedPhotos = await uploadFilesToServer(selectedFiles);
        
        // Handle both photo objects and URLs
        newPhotos = uploadedPhotos.map(photo => {
          if (typeof photo === 'string') {
            return {
              url: photo,
              description: photoDescription || ''
            };
          } else {
            return photo;
          }
        });
      } else {
        if (!newPhotoUrl) {
          setSaveError('Please enter a photo URL');
          setSavingChanges(false);
          return;
        }
        
        newPhotos = [{
          url: newPhotoUrl,
          description: photoDescription || ''
        }];
      }
      
      // Add to the photos array
      const updatedPhotos = [...photos];
      
      // For display purposes, ensure absolute URLs
      const displayPhotos = newPhotos.map(photo => ({
        ...photo,
        url: ensureAbsoluteUrl(photo.url)
      }));
      
      // Update memorial with new photos - keep the original URL format for storage
      await api.patch(`/api/memorials/${id}`, {
        photos: [
          ...updatedPhotos.map(p => {
            // Remove API base URL if it's an absolute URL
            const url = p.url.startsWith(apiBaseUrl) ? 
              p.url.substring(apiBaseUrl.length) : 
              p.url;
            
            return {
              ...p,
              url: url
            };
          }), 
          ...newPhotos
        ]
      });
      
      // Update state with the display URLs
      setPhotos([...updatedPhotos, ...displayPhotos]);
      
      // Clean up preview URLs
      previewUrls.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      setPreviewUrls([]);
      setSelectedFiles([]);
      
      handleClosePhotoDialog();
    } catch (err) {
      console.error('Error adding photos:', err);
      setSaveError(`Failed to add photos: ${err.response?.data?.message || err.message}`);
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDeletePhoto = async (index) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }
    
    try {
      setSavingChanges(true);
      
      // Remove photo from array
      const updatedPhotos = photos.filter((_, i) => i !== index);
      
      // Update memorial with new photos - convert display URLs back to storage format
      await api.patch(`/api/memorials/${id}`, {
        photos: updatedPhotos.map(p => {
          // Remove API base URL if it's an absolute URL
          const url = p.url.startsWith(apiBaseUrl) ? 
            p.url.substring(apiBaseUrl.length) : 
            p.url;
          
          return {
            ...p,
            url: url
          };
        })
      });
      
      // Update state
      setPhotos(updatedPhotos);
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert(`Failed to delete photo: ${err.response?.data?.message || err.message}`);
    } finally {
      setSavingChanges(false);
    }
  };

  const handleMovePhoto = async (index, direction) => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === photos.length - 1)
    ) {
      return;
    }
    
    try {
      setSavingChanges(true);
      
      // Create a copy of the photos array
      const updatedPhotos = [...photos];
      
      // Swap photos
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      const temp = updatedPhotos[index];
      updatedPhotos[index] = updatedPhotos[newIndex];
      updatedPhotos[newIndex] = temp;
      
      // Update memorial with new photos order - convert display URLs back to storage format
      await api.patch(`/api/memorials/${id}`, {
        photos: updatedPhotos.map(p => {
          // Remove API base URL if it's an absolute URL
          const url = p.url.startsWith(apiBaseUrl) ? 
            p.url.substring(apiBaseUrl.length) : 
            p.url;
          
          return {
            ...p,
            url: url
          };
        })
      });
      
      // Update state
      setPhotos(updatedPhotos);
    } catch (err) {
      console.error('Error reordering photos:', err);
      alert(`Failed to reorder photos: ${err.response?.data?.message || err.message}`);
    } finally {
      setSavingChanges(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading memorial...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <IconButton onClick={goBack}>
            <ArrowBackIcon />
            <Typography sx={{ ml: 1 }}>Go Back</Typography>
          </IconButton>
        </Box>
      </Container>
    );
  }

  if (!memorial) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Memorial not found</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <IconButton onClick={goBack}>
            <ArrowBackIcon />
            <Typography sx={{ ml: 1 }}>Go Back</Typography>
          </IconButton>
        </Box>
      </Container>
    );
  }

  // Get profile photo (first photo or placeholder)
  const profilePhoto = photos && photos.length > 0 ? photos[0].url : 'https://via.placeholder.com/150';

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <IconButton onClick={goBack}>
          <ArrowBackIcon />
        </IconButton>
        
        <Box>
          <Button 
            variant={editingDetails ? "contained" : "outlined"}
            color={editingDetails ? "success" : "secondary"}
            startIcon={editingDetails ? <SaveIcon /> : <EditIcon />}
            onClick={toggleDetailsEditing}
            sx={{ mr: 1 }}
          >
            {editingDetails ? "Save Details" : "Edit Details"}
          </Button>
          
          <Button 
            variant={isEditing ? "contained" : "outlined"} 
            color={isEditing ? "success" : "primary"}
            startIcon={<EditIcon />}
            onClick={toggleEditMode}
          >
            {isEditing ? "Done Editing" : "Edit Photos"}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
        {/* Profile Header with Photo and Info */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, mb: 3 }}>
          <Avatar 
            src={profilePhoto} 
            alt={memorial.name}
            sx={{ 
              width: { xs: 120, sm: 150 }, 
              height: { xs: 120, sm: 150 }, 
              border: '2px solid #e0e0e0',
              mr: { xs: 0, sm: 4 },
              mb: { xs: 2, sm: 0 }
            }}
          />
          <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            {editingDetails ? (
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={editForm.name}
                onChange={handleEditFormChange}
                variant="outlined"
                margin="dense"
                sx={{ mb: 1 }}
              />
            ) : (
              <Typography variant="h5" fontWeight="bold">
                {memorial.name}
              </Typography>
            )}
            
            {editingDetails ? (
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="Birth Date"
                  name="dateOfBirth"
                  type="date"
                  value={editForm.dateOfBirth}
                  onChange={handleEditFormChange}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  margin="dense"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Death Date"
                  name="dateOfDeath"
                  type="date"
                  value={editForm.dateOfDeath}
                  onChange={handleEditFormChange}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  margin="dense"
                  sx={{ flex: 1 }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formatDate(memorial.dateOfBirth)} - {formatDate(memorial.dateOfDeath)}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', mt: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ mr: 3, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold">{photos.length}</Typography>
                <Typography variant="body2" color="text.secondary">Photos</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Biography Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Biography</Typography>
          
          {editingDetails ? (
            <TextField
              fullWidth
              label="Biography"
              name="biography"
              value={editForm.biography}
              onChange={handleEditFormChange}
              multiline
              rows={8}
              variant="outlined"
            />
          ) : (
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.7
              }}
            >
              {memorial.biography}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Photos Gallery */}
        <Box sx={{ mt: 3, position: 'relative' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2  }}>Photos</Typography>
          
          {photos && photos.length > 0 ? (
            <Grid container spacing={2}>
              {photos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ 
                    height: 0, 
                    paddingTop: '100%', 
                    position: 'relative',
                    borderRadius: 1,
                    boxShadow: 2
                  }}>
                    <CardMedia
                      component="img"
                      image={photo.url}
                      alt={photo.description || `Photo ${index + 1}`}
                      sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    
                    {/* Edit controls overlay */}
                    {isEditing && (
                      <Box sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <Box>
                          <Tooltip title="Delete photo">
                            <IconButton 
                              color="error" 
                              onClick={() => handleDeletePhoto(index)}
                              disabled={savingChanges}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <Box sx={{ mt: 1 }}>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleMovePhoto(index, 'up')}
                                disabled={index === 0 || savingChanges}
                                size="small"
                              >
                                <ArrowUpwardIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title="Move down">
                            <span>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleMovePhoto(index, 'down')}
                                disabled={index === photos.length - 1 || savingChanges}
                                size="small"
                              >
                                <ArrowDownwardIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </Card>
                  {photo.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                      {photo.description}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">No photos available</Typography>
            </Box>
          )}
          
          {/* Add photo button (only visible in edit mode) */}
          {isEditing && (
            <Fab 
              color="primary" 
              aria-label="add photo"
              onClick={handleOpenPhotoDialog}
              sx={{ 
                position: 'fixed',
                bottom: 20,
                right: 20
              }}
            >
              <AddIcon />
            </Fab>
          )}
        </Box>
      </Paper>
      
      {/* Add Photo Dialog */}
      <Dialog 
        open={openPhotoDialog} 
        onClose={handleClosePhotoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Photos</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          
          {/* Upload method toggle */}
          <Box sx={{ display: 'flex', mb: 3, mt: 1 }}>
            <Button
              variant={uploadMethod === 'file' ? 'contained' : 'outlined'}
              startIcon={<CloudUploadIcon />}
              onClick={() => handleUploadMethodChange('file')}
              sx={{ mr: 1, flex: 1 }}
            >
              Upload Files
            </Button>
            <Button
              variant={uploadMethod === 'url' ? 'contained' : 'outlined'}
              startIcon={<LinkIcon />}
              onClick={() => handleUploadMethodChange('url')}
              sx={{ flex: 1 }}
            >
              Use URL
            </Button>
          </Box>
          
          {uploadMethod === 'file' ? (
            // File upload option
            <Box>
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current.click()}
                fullWidth
                sx={{ mb: 2 }}
              >
                Choose Photos
              </Button>
              
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Selected: {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} 
                    ({Math.round(selectedFiles.reduce((total, file) => total + file.size, 0) / 1024)} KB total)
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {previewUrls.map((url, index) => (
                      <Grid item xs={4} sm={3} md={2} key={index}>
                        <Box sx={{ 
                          height: 0, 
                          paddingTop: '100%', 
                          position: 'relative',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid #ddd'
                        }}>
                          <img 
  src={url} 
  alt={`Preview ${index + 1}`}
  loading="lazy"
  style={{ 
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              {uploadProgress > 0 && savingChanges && (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Uploading: {uploadProgress}%
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            // URL option
            <TextField
              margin="dense"
              label="Photo URL"
              type="url"
              fullWidth
              variant="outlined"
              value={newPhotoUrl}
              onChange={(e) => setNewPhotoUrl(e.target.value)}
              required
              helperText="Enter the URL of the photo"
              sx={{ mb: 2 }}
            />
          )}
          
          <TextField
            margin="dense"
            label="Photo Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={photoDescription}
            onChange={(e) => setPhotoDescription(e.target.value)}
            helperText={uploadMethod === 'file' && selectedFiles.length > 1 ? 
              "This description will be applied to all uploaded photos" : 
              "Add a description for this photo"}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhotoDialog}>Cancel</Button>
          <Button 
  onClick={handleAddPhoto} 
  variant="contained" 
  color="primary"
  disabled={savingChanges || (uploadMethod === 'file' ? !selectedFiles.length : !newPhotoUrl)}
>
  {savingChanges ? 'Adding...' : 'Add Photo' + (uploadMethod === 'file' && selectedFiles.length > 1 ? 's' : '')}
</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditMemorial;